// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react';
import './Labeling.scss';

import { firestore, storage } from '../context/firebase';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';

// Importing custom pages
import LabelingRequests from '../shared_components/LabelingRequests/LabelingRequests';
import BaleView from '../components/BaleView/BaleView';

// Importing icons
import back_icon from '../assets/icons/white/back_small.png';
import { OBSERVATION_DOCUMENT_PATH } from '../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../stores/authStore';

export default function Labeling() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.currentUser);
  const [currentObservation, setCurrentObservation] = React.useState(null);
  const [observations, setObservations] = React.useState({});
  const [activeObservationId, setActiveObservationId] = React.useState(null);
  const LabelingRequestHandle = React.useRef(null);

  function onClose() {
    navigate(-1);
  }

  function getNextObservation() {
    /* Get the next observation */
    LabelingRequestHandle.current.getNextObservation(currentObservation);
  }

  function getPreviousObservation() {
    /* Get the previous observation */
    LabelingRequestHandle.current.getPreviousObservation(currentObservation);
  }

  function onSetActiveObservationId(id) {
    setActiveObservationId(id);
    setCurrentObservation(observations[id]);
  }

  async function handleFieldChange(event) {
    /* Update properties of a bale */
    const { name, value } = event.target;
    const observationRef = doc(firestore, OBSERVATION_DOCUMENT_PATH(currentUser.company.company, activeObservationId));
    await updateDoc(observationRef, { [name]: value });
    setCurrentObservation((prevState) => {
      return {
        ...prevState,
        [name]: value,
      };
    });
  }

  React.useEffect(() => {
    // Get all observations
    const observationsNew = LabelingRequestHandle.current.getObservations();
    setObservations(observationsNew);
    // Find key of currentObservation in observations. Use imageStoragePath as unique identifier
    const observationKey = Object.keys(observationsNew).find(
      (key) => observationsNew[key]?.image_storage_path === currentObservation?.image_storage_path,
    );
    setActiveObservationId(observationKey);
  }, [currentObservation]);

  return (
    <div className='labeling'>
      {!currentObservation && (
        <div className='labeling-header'>
          <div onClick={onClose} className='labeling-back'>
            <img src={back_icon} alt='Back' className='labeling-back-icon' />
          </div>
          <h3 className='labeling-title'>{t('labeling_title')}</h3>
        </div>
      )}
      <div className='labeling-requests-container' style={{ display: currentObservation ? 'none' : 'block' }}>
        <LabelingRequests
          ref={LabelingRequestHandle}
          firestore={firestore}
          storage={storage}
          currentUser={currentUser}
          currentObservation={currentObservation}
          setCurrentObservation={setCurrentObservation}
          paginationSamples={30}
        />
      </div>
      {currentObservation && activeObservationId && (
        <BaleView
          bales={observations}
          activeBaleId={activeObservationId}
          setActiveBaleId={onSetActiveObservationId}
          getNextBale={getNextObservation}
          getPreviousBale={getPreviousObservation}
          handleFieldChange={handleFieldChange}
          onCloseBaleView={() => {
            setCurrentObservation(null);
          }}
          activeBaleDocRef={doc(firestore, OBSERVATION_DOCUMENT_PATH(currentUser.company.company, activeObservationId))}
        />
      )}
    </div>
  );
}
